import type { GridColDef } from '@mui/x-data-grid';
import type { CommitmentUsageReport } from '@vy/types';
import { isNotNullOrUndefined, vyDate, type ItemOf, type ValueOf } from '@vy/utils';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { getFormattedNumberWithCurrencySymbol } from '../../../../../services/currency-symbol-mapper';
import { formatDate } from '../../../../../utils/date';
import { formatNumberWithPrecision, formatPercentage } from '../../../../../utils/number';

export const COMMITMENT_USAGE_REPORT_KEYS = Object.freeze({
  CUSTOMER_NAME: 'customerName',
  ERP_ID: 'erpId',
  CONSUMED_UNITS: 'commitmentConsumptionUnitsAmount',
  PRODUCT_VARIANT_NAME: 'productVariantName',
  CONTRACT_START_DATE: 'contractStartDate',
  CONTRACT_END_DATE: 'contractEndDate',
  DAYS_TO_CONTRACT_END_DATE: 'daysToContractEnd',
  MAXIMUM_COMMITMENT: 'totalCommitmentCurrencyAmount',
  TOTAL_COMMITMENT_CONSUMED: 'commitmentConsumptionCurrencyAmount',
  USAGE_PERCENTAGE: 'commitmentConsumptionPercentage',
  COMMERCIAL_TERMS_AMOUNT: 'commercialTermsAmount',
  TOTAL_COMMITMENT_UNITS: 'totalCommitmentUnitsAmount',
  CURRENCY: 'currency',
  STATUS: 'contractStatus',
  CONTRACT_NAME: 'contractName',
} satisfies Record<string, keyof ItemOf<CommitmentUsageReport>>);

export type CommitmentUsageReportColumnKeys = ValueOf<typeof COMMITMENT_USAGE_REPORT_KEYS>;

export const COMMITMENT_USAGE_REPORT_COLUMNS: GridColDef<ItemOf<CommitmentUsageReport>>[] = [
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value, row) => row.customerName,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ value }) => <VyTableCell value={value} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.ERP_ID,
    headerName: 'ERP ID',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 75,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => <VyTableCell value={row.erpId ? row.erpId.slice(-5) : '--'} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.PRODUCT_VARIANT_NAME,
    headerName: 'Product',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ value }) => <VyTableCell value={value} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.STATUS,
    headerName: 'Status',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 100,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ value }) => <VyTableCell value={value} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.CONTRACT_NAME,
    headerName: 'Contract name',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value, row) => row.contractName,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ value }) => <VyTableCell value={value} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.CONTRACT_START_DATE,
    headerName: 'Contract start day',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => vyDate(value).format('YYYY-MM-DD'),
    renderCell: ({ value }) => <VyTableCell value={formatDate(value)} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.CONTRACT_END_DATE,
    headerName: 'Contract end day',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => vyDate(value).format('YYYY-MM-DD'),
    renderCell: ({ value }) => <VyTableCell value={isNotNullOrUndefined(value) ? formatDate(value) : '--'} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.DAYS_TO_CONTRACT_END_DATE,
    headerName: 'Days to end',
    headerClassName: 'text-sm',
    flex: 1,

    minWidth: 125,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row: { daysToContractEnd } }) => <VyTableCell value={daysToContractEnd ?? 'Unlimited'} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.MAXIMUM_COMMITMENT,
    headerName: 'Minimum commitment',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ value, row }) => (
      <VyTableCell value={getFormattedNumberWithCurrencySymbol(row.currency, value)} />
    ),
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.TOTAL_COMMITMENT_CONSUMED,
    headerName: 'Total consumed',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ value, row }) => (
      <VyTableCell value={getFormattedNumberWithCurrencySymbol(row.currency, value)} />
    ),
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.TOTAL_COMMITMENT_UNITS,
    headerName: 'Quantity commitment',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { totalCommitmentUnitsAmount } }) => (
      <VyTableCell value={formatNumberWithPrecision(totalCommitmentUnitsAmount, 2)} />
    ),
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.CONSUMED_UNITS,
    headerName: 'Consumed (Quantity)',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ value }) => <VyTableCell value={formatNumberWithPrecision(value, 2)} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.USAGE_PERCENTAGE,
    headerName: '% usage',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 100,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ value }) => <VyTableCell value={formatPercentage(value)} />,
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.COMMERCIAL_TERMS_AMOUNT,
    headerName: 'Commercials',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ value, row }) => (
      <VyTableCell value={getFormattedNumberWithCurrencySymbol(row.currency, value)} />
    ),
  },
  {
    field: COMMITMENT_USAGE_REPORT_KEYS.CURRENCY,
    headerName: 'Currency',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 100,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => <VyTableCell value={row.currency} />,
  },
];
